import {animalSurface, animalGroundAt} from './animal-surface.js';
import {WORLD_UNITS_PER_METER} from './world-scale.js';
import {birdBranchPoint, birdRouteEnvelope, BIRD_ROCK_RADIUS} from './bird-host-clearance.js';

const freeze = value => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};
const fail = message => { throw new TypeError(message); };
const finite = (n, label) => Number.isFinite(n) ? n : fail(`Invalid ${label}`);
const positive = (n, label) => finite(n, label) > 0 ? n : fail(`Invalid ${label}`);
const vector = (p, label) => {
  if (!Array.isArray(p) || p.length !== 3 || ![0, 1, 2].every(i => Object.hasOwn(p, i) && Number.isFinite(p[i]))) fail(`Invalid ${label}`);
  return [...p];
};
const list = (p, label, maximum = 1000) => {
  if (!Array.isArray(p) || p.length > maximum || !Array.from({length:p.length}, (_, i) => Object.hasOwn(p, i)).every(Boolean)) fail(`Missing or invalid ${label}`);
  return p;
};
const bounds = (b, label) => {
  const min = vector(b?.min, `${label} minimum`), max = vector(b?.max, `${label} maximum`);
  if (min.some((v, i) => v >= max[i])) fail(`Invalid ${label} extent`);
  return {min, max};
};
const overlap = (a, b) => a.min.every((v, i) => v <= b.max[i] && a.max[i] >= b.min[i]);
const box = (center, radius) => ({min:center.map(v => v - radius), max:center.map(v => v + radius)});
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const EPS = 2e-6;

/** This is a candidate-only finite study. No population, asset, RNG, route or
 * renderer state is changed. The source sweep ALREADY contains 0.204 m travel.
 * A runtime must not add that displacement a second time or apply pitch/roll. */
export const MALLARD_PILOT_LIMITS = freeze({
  revision:'mallard-finite15a-water-placement-v1', candidateOnly:true,
  worldUnitsPerMetre:WORLD_UNITS_PER_METER, travelMetres:.204,
  waterlineMetres:.130, waterMin:.169, waterMax:.211, waveExcursion:.01015,
  initialPitch:0, initialRoll:0, glassX:5.56, glassZ:4.02,
  bedMargin:.002, obstacleMargin:.002, maxCandidates:24,
});
export const MALLARD_PROFILE15A_SWEPT_BOUNDS = freeze({
  min:[-.09521560370922089, -.06158331781625748, -.41479045152664185],
  max:[.09522151947021484, .3298454284667969, .2750000059604645],
});
export const MALLARD_PROFILE15A_SHA256 = 'ce5988b4338b821e2b913b2ddad5a84c2f0cd4d7499c3c01af4be5cc636078ff';
const CANDIDATE_OFFSETS = freeze([
  [.85,.55], [1.15,.45], [.65,.55], [1.40,.30], [.80,.25], [1.15,.65],
  [.50,.70], [1.50,.60], [.95,.75], [1.65,.35], [.55,.35], [1.30,.10],
  [.75,-.05], [1.70,.65], [1.85,.15], [.40,.10], [1.50,-.15], [1.90,.45],
  [.25,.65], [1.05,-.15], [1.70,-.15], [.30,.35], [1.85,-.35], [.60,-.25],
]);

/** Union measured FULL and PROXY finite-sequence bounds before calling the
 * planner. A quality/load choice is deliberately not a planner input. */
export function unionMallardSweptBounds(...measured) {
  if (!measured.length) fail('Missing measured mallard swept bounds');
  const all = measured.map((b, i) => bounds(b, `mallard sweep ${i}`));
  return freeze({min:[0,1,2].map(i => Math.min(...all.map(b => b.min[i]))), max:[0,1,2].map(i => Math.max(...all.map(b => b.max[i])))});
}

function profileBounds(profile, supplied) {
  if (!profile || profile.kind !== 'mallard-physical-float-study-profile' || profile.schemaVersion !== 1 || profile.assetSHA256 !== MALLARD_PROFILE15A_SHA256) fail('Missing reviewed profile15a float profile');
  if (profile.units !== 'metres' || profile.up !== '+Y' || profile.forward !== '-Z' || profile.float?.waterlineYMetres !== .130 || profile.float.neutralBodyPitchRadians !== 0 || profile.float.neutralBodyRollRadians !== 0) fail('Invalid profile15a float alignment');
  const authored = bounds(profile.authoredSweptBounds?.['floating-paddle-idle'], 'profile15a float sweep');
  if (['min','max'].some(k => authored[k].some((v,i) => v !== MALLARD_PROFILE15A_SWEPT_BOUNDS[k][i]))) fail('Unexpected profile15a float sweep');
  const shared = bounds(supplied, 'shared full/proxy measured swept bounds');
  if (authored.min.some((v,i) => shared.min[i] > v || shared.max[i] < authored.max[i])) fail('Shared measured sweep does not contain profile15a');
  if (shared.min.some((v,i) => Math.abs(v) > 2 || Math.abs(shared.max[i]) > 2)) fail('Unbounded mallard sweep');
  return shared;
}

/** All vertices of cells intersecting the rectangle enclose the complete exact
 * animalGroundAt triangle surface, including ridges BETWEEN point probes. */
function groundRange(surface, b) {
  const {nx,nz,heights} = surface;
  const x0 = clamp(Math.floor((b.min[0]+6)*nx/12),0,nx), x1 = clamp(Math.ceil((b.max[0]+6)*nx/12),0,nx);
  const z0 = clamp(Math.floor((b.min[2]+4.5)*nz/9),0,nz), z1 = clamp(Math.ceil((b.max[2]+4.5)*nz/9),0,nz);
  let min = Infinity, max = -Infinity;
  for (let j=z0;j<=z1;j++) for (let i=x0;i<=x1;i++) {
    const y = finite(heights[j*(nx+1)+i], 'rendered terrain probe');
    min = Math.min(min,y); max = Math.max(max,y);
  }
  return {min,max};
}
function lakePolygon(terrain) {
  const lake = terrain.layout.lake;
  for (const k of ['x','z','rx','rz']) finite(lake[k], `lake ${k}`);
  positive(lake.rx,'lake radius'); positive(lake.rz,'lake radius');
  // Exact outer ring and Float32 positions in World3D.buildWater, before waves.
  return Array.from({length:112},(_,i) => {
    const a=i*Math.PI*2/112, r=positive(terrain.lakeBoundary(a),'lake boundary')*1.10;
    return [Math.fround(clamp(lake.x+Math.cos(a)*lake.rx*r,-5.995,5.995)), Math.fround(clamp(lake.z+Math.sin(a)*lake.rz*r,-4.506,4.506))];
  });
}
function pointInPolygon(x,z,polygon) {
  let inside=false;
  for(let i=0,j=polygon.length-1;i<polygon.length;j=i++) {
    const a=polygon[i],b=polygon[j];
    if ((a[1]>z)!==(b[1]>z) && x<(b[0]-a[0])*(z-a[1])/(b[1]-a[1])+a[0]) inside=!inside;
  }
  return inside;
}
function segmentMeetsRectangle(a,b,loX,hiX,loZ,hiZ) {
  let lo=0,hi=1;
  for (const [origin,delta,min,max] of [[a[0],b[0]-a[0],loX,hiX],[a[1],b[1]-a[1],loZ,hiZ]]) {
    if (!delta) { if(origin<min||origin>max)return false; continue; }
    const t0=(min-origin)/delta,t1=(max-origin)/delta;
    lo=Math.max(lo,Math.min(t0,t1)); hi=Math.min(hi,Math.max(t0,t1));
    if(lo>hi)return false;
  }
  return true;
}
function lakeContainsRectangle(polygon,b) {
  const [loX,hiX,loZ,hiZ]=[b.min[0]-EPS,b.max[0]+EPS,b.min[2]-EPS,b.max[2]+EPS];
  if(![loX,hiX].every(x=>[loZ,hiZ].every(z=>pointInPolygon(x,z,polygon))))return false;
  return !polygon.some((p,i)=>segmentMeetsRectangle(p,polygon[(i+1)%polygon.length],loX,hiX,loZ,hiZ));
}
function routeRectangle(route) {
  for(const key of ['x','z','rx','rz','angle']) finite(route?.[key],`resident route ${key}`);
  if(route.rx<0||route.rz<0)fail('Invalid resident route radius');
  const wobble=Math.abs(finite(route.wobble??0,'route wobble')), x=route.rx+wobble,z=route.rz+wobble*.6,c=Math.abs(Math.cos(route.angle)),s=Math.abs(Math.sin(route.angle));
  return {min:[route.x-c*x-s*z,0,route.z-s*x-c*z],max:[route.x+c*x+s*z,0,route.z+s*x+c*z]};
}
function addColumn(obstacles,id,kind,x,z,radius) {
  finite(x,`${kind} x`);finite(z,`${kind} z`);positive(radius,`${kind} radius`);
  obstacles.push({id,kind,min:[x-radius,-100,z-radius],max:[x+radius,100,z+radius]});
}
function addBox(obstacles,id,kind,b) { obstacles.push({id:String(id),kind,...bounds(b,kind)}); }
function staticObstacles(terrain,flora,biodiversity) {
  const obstacles=[],{cabin,jetty}=terrain.layout;
  if(!cabin||!jetty)fail('Missing cabin/jetty descriptors');
  for(const p of [cabin,jetty]){finite(p.x,'prop x');finite(p.z,'prop z');}
  // Larger than the displayed roof/foundation. The path is not a raised solid.
  addColumn(obstacles,'cabin','cabin',cabin.x,cabin.z,positive(cabin.scale,'cabin scale')*1.65);
  const sx=positive(jetty.scaleX,'jetty scaleX'),sz=positive(jetty.scaleZ,'jetty scaleZ');
  addBox(obstacles,'jetty','jetty',{min:[jetty.x-.425*sx,-.23,jetty.z-.10*sz],max:[jetty.x+.425*sx,.39,jetty.z+(11*.16+.10)*sz]});
  // Rotation-invariant sphere around the complete .8-scale hull/benches,
  // expanded for Y=.27±.012 and any displayed roll. Includes tether separately.
  const boat=box([3.94,.27,2.44],.50); boat.min[1]-=.012;boat.max[1]+=.012;
  addBox(obstacles,'boat','boat',boat);
  addBox(obstacles,'boat-rope','boat tether',{min:[3.668,.198,2.058],max:[3.942,.362,2.202]});
  list(flora?.rocks,'rock descriptors',170).forEach((r,i)=>addBox(obstacles,`rock-${i}`,'rock',box([finite(r.x,'rock x'),finite(r.y,'rock y'),finite(r.z,'rock z')],positive(r.scale,'rock scale')*BIRD_ROCK_RADIUS+EPS)));
  list(flora?.trees,'tree descriptors',88).forEach((t,i)=>{
    const scale=positive(t.scale,'tree scale'),x=finite(t.x,'tree x'),y=finite(t.y,'tree y'),z=finite(t.z,'tree z');
    // Full trunk and authored crown, including all weather rotation. A 2.3
    // local-unit root sphere covers both tree meshes and seasonal contraction.
    addBox(obstacles,`tree-${i}`,'tree',box([x,y,z],2.3*scale));
  });
  // Complete local plant geometry is within .80 of its root; rotations preserve
  // that norm and seasonal contraction cannot increase it. Deliberately coarse.
  list(biodiversity.plants,'plant descriptors',131).forEach((p,i)=>addBox(obstacles,`plant-${i}`,'plant',box([finite(p.x,'plant x'),finite(p.y,'plant y')+.008,finite(p.z,'plant z')],positive(p.scale,'plant scale')*.80+EPS)));
  for(const support of list(biodiversity.birdSupports??[],'bird support descriptors',8)) for(const [i,seg] of list(support.segments,'bird support segments',30).entries()) {
    const a=vector(seg.a,'support start'),b=vector(seg.b,'support end'),r=Math.max(positive(seg.radius0,'support radius'),positive(seg.radius1,'support radius'))+EPS;
    addBox(obstacles,`${support.id}-${i}`,'bird deadwood',{min:a.map((v,j)=>Math.min(v,b[j])-r),max:a.map((v,j)=>Math.max(v,b[j])+r)});
  }
  // These rocks use World3D's private RNG. Reserve the entire possible stream
  // placement corridor, rather than consuming or reconstructing that stream.
  let previous;
  for(let i=0;i<=512;i++) {
    const p=terrain.riverSample(i/512);for(const key of ['x','z','width'])finite(p?.[key],`stream ${key}`);
    if(previous) {
      const reach=Math.max(p.width,previous.width)+9.1/512+.001+.165+BIRD_ROCK_RADIUS*.102;
      addBox(obstacles,`stream-rocks-${i}`,'stream rock corridor',{min:[Math.min(p.x,previous.x)-reach,-100,Math.min(p.z,previous.z)-reach],max:[Math.max(p.x,previous.x)+reach,100,Math.max(p.z,previous.z)+reach]});
    }
    previous=p;
  }
  return obstacles;
}

function residentObstacles(animals,biodiversity,surface) {
  const obstacles=[],omitted=[];
  for(const a of list(animals,'animal descriptors',10)) {
    if(a.type==='fish') {
      const r=routeRectangle(a.route),b=a.body;
      // |vertex| + 2 sum(|pivot|) bounds every concatenated rigid rotation,
      // and normalized skin weights preserve the bound. The published carp
      // full/proxy fixture test verifies a .60 local-unit radius for all roles.
      const scale=Math.max(positive(b?.width,'fish width')/.039,positive(b?.height,'fish height')/.068,positive(b?.length,'fish length')/.205),radius=.60*scale;
      const bed=groundRange(surface,r),depth=positive(a.depth,'fish depth');
      const minRoot=Math.max(bed.min+b.height*.62,MALLARD_PILOT_LIMITS.waterMin-depth-.012-.018);
      const maxRoot=Math.max(bed.max+b.height*.62,MALLARD_PILOT_LIMITS.waterMax-depth+.012);
      addBox(obstacles,a.id,'fish 3D sweep',{min:[r.min[0]-radius,minRoot-radius,r.min[2]-radius],max:[r.max[0]+radius,maxRoot+radius,r.max[2]+radius]});
    } else if(a.type==='rabbit'||a.type==='deer') {
      const h=a.home;
      addColumn(obstacles,a.id,'mammal reserved home',h?.x,h?.z,h?.radius);
    } else fail(`Uncovered original animal: ${String(a.type)}`);
  }
  for(const a of list(biodiversity.fauna,'fauna descriptors',32)) {
    if(a.type==='frog') {
      const p=a.frogBankPlan;
      if(p) {
        if(typeof p.safe!=='boolean')fail('Invalid frog plan safety');
        if(!p.safe){omitted.push({id:`frog-${a.id}`,reason:'Original frog is omitted by its own plan'});continue;}
        const radius=positive(p.sweptRadius,'frog swept radius');
        addBox(obstacles,`frog-${a.id}`,'frog route',{min:[Math.min(finite(p.homeX,'frog home'),finite(p.endX,'frog end'))-radius,-100,Math.min(finite(p.homeZ,'frog home'),finite(p.endZ,'frog end'))-radius],max:[Math.max(p.homeX,p.endX)+radius,100,Math.max(p.homeZ,p.endZ)+radius]});
      }else {
        // Legacy frogRoute can search <=.40 from the immutable descriptor;
        // original source-rig bank search can add <=.40. Column also includes
        // finite hop and a deliberately enlarged whole-body sphere.
        const scale=positive(a.scale,'frog scale'),travel=(.10+positive(a.radius,'frog route radius')*.13)*scale;
        addColumn(obstacles,`frog-${a.id}`,'frog conservative route',a.x,a.z,.80+travel+scale);
      }
    } else if(['robin','wren','swallow'].includes(a.type)) {
      const p=a.birdPlan;
      if(!p||typeof p.safe!=='boolean')fail(`Missing original ${a.type} route plan`);
      if(!p.safe){omitted.push({id:`${a.type}-${a.id}`,reason:'Original bird is omitted by its own plan'});continue;}
      let anchor=vector(p.anchor,'bird anchor'),hostReach=0;
      if(p.host) {
        const host=p.host,local=birdBranchPoint(host,p.branch);
        anchor=[finite(host.x,'bird host x'),finite(host.y,'bird host y')+.008,finite(host.z,'bird host z')];
        // Enclose ALL allowed sway/season values rather than sampling phases.
        hostReach=Math.hypot(...local)*positive(host.scale,'bird host scale');
      }
      const e=birdRouteEnvelope(p,0,1),center=vector(e.center,'bird envelope center').map((v,i)=>v+anchor[i]);
      addBox(obstacles,`${a.type}-${a.id}`,'bird complete route',box(center,positive(e.radius,'bird envelope radius')+hostReach));
    } else if(!['butterfly','dragonfly'].includes(a.type))fail(`Uncovered fauna type: ${String(a.type)}`);
  }
  return {obstacles,omitted};
}

function transformedSweep(b,scale,x,z,heading) {
  const c=Math.cos(heading),s=Math.sin(heading),xs=[],zs=[];
  for(const xx of [b.min[0],b.max[0]]) for(const zz of [b.min[2],b.max[2]]) {
    xs.push(x+(c*xx+s*zz)*scale);zs.push(z+(-s*xx+c*zz)*scale);
  }
  const limits=MALLARD_PILOT_LIMITS;
  return {min:[Math.min(...xs),limits.waterMin-limits.waveExcursion+(b.min[1]-limits.waterlineMetres)*scale,Math.min(...zs)],max:[Math.max(...xs),limits.waterMax+limits.waveExcursion+(b.max[1]-limits.waterlineMetres)*scale,Math.max(...zs)]};
}
const scope = () => ({
  candidateOnly:true, universalCollisionClaim:false,
  covered:['complete supplied full/proxy finite15a swept box','actual 112-sector lake footprint','full water range and ±0.01015 wave excursion','exact animalGroundAt heightfield cell maxima','glass','cabin, jetty, boat and tether','flora rocks and conservative complete tree/plant bounds','all possible private-RNG stream rock placements','original fish 3D sweeps','reserved mammal homes','frog swept routes or conservative legacy columns','bird complete routes, swaying hosts and deadwood'],
  notCertified:['butterfly, dragonfly and firefly simultaneous encounters','decorative grass, flora flower stems and weather particles','camera visibility and natural foliage occlusion','hydrodynamic buoyancy, mass and propulsion','biological cadence','other clips, repeated cycles, shore transitions, flight, pitch or roll','replacement original-resident geometry or changed world construction'],
  profileScope:'illustrative 0.130 m waterline; paddles may be partly above water during recovery',
});

/** All required scene data must exist synchronously before loading the candidate.
 * animals is createAnimals(...).animals; biodiversity is createBiodiversity(...).
 * Missing/invalid input or asynchronous/invalid probes OMIT rather than placing.
 * extraObstacles are optional world-space swept AABBs, never a replacement for
 * the original-resident checks. A probe can only reject an already safe choice. */
export function planMallardPilot({seed,terrain,flora,animals,biodiversity,profile,sweptBoundsMetres,individualScale=1,candidates,probe,extraObstacles=[]}={}) {
  const base={id:'phase2:mallard:0',species:'mallard',revision:MALLARD_PILOT_LIMITS.revision,candidateOnly:true,seed:Number.isSafeInteger(seed)?seed:null,safe:false,accepted:false,testedCandidates:0,rejections:[],scope:scope()};
  try {
    if(!Number.isSafeInteger(seed)||seed<0||seed>0xffffffff)fail('Invalid unsigned world seed');
    if(!terrain||typeof terrain.heightAt!=='function'||typeof terrain.lakeBoundary!=='function'||typeof terrain.riverSample!=='function'||!terrain.layout?.lake)fail('Missing authoritative terrain probes');
    if(terrain.seed!==undefined&&terrain.seed!==seed)fail('Terrain seed mismatch');
    if(!biodiversity)fail('Missing original biodiversity descriptors');
    if(probe!==undefined&&typeof probe!=='function')fail('Invalid candidate probe');
    if(!Number.isFinite(individualScale)||individualScale<.85||individualScale>1.15)fail('Invalid fixed individual scale');
    const shared=profileBounds(profile,sweptBoundsMetres),scale=WORLD_UNITS_PER_METER*individualScale,surface=animalSurface(terrain),polygon=lakePolygon(terrain);
    const resident=residentObstacles(animals,biodiversity,surface),obstacles=[...staticObstacles(terrain,flora,biodiversity),...resident.obstacles];
    list(extraObstacles,'extra obstacles',256).forEach((b,i)=>{
      if(!b||typeof b!=='object'||Array.isArray(b)||(b.id!==undefined&&(typeof b.id!=='string'||!b.id.trim())))fail('Invalid extra obstacle descriptor');
      addBox(obstacles,b.id??`extra-${i}`,'extra obstacle',b);
    });
    const lake=terrain.layout.lake;
    // A stateless seed permutation; never call the original world RNG or use
    // population/load order. No changed seed or resident is silently substituted.
    const offset=((Math.imul(seed^0x6d616c6c,0x45d9f3b)>>>0)%CANDIDATE_OFFSETS.length);
    const choices=candidates===undefined?CANDIDATE_OFFSETS.map((_,i)=>{const p=CANDIDATE_OFFSETS[(i+offset)%CANDIDATE_OFFSETS.length];return{x:lake.x+p[0],z:lake.z+p[1],heading:0};}):list(candidates,'candidate list',MALLARD_PILOT_LIMITS.maxCandidates);
    for(const p of choices) {
      const x=finite(p?.x,'candidate x'),z=finite(p?.z,'candidate z'),heading=finite(p?.heading??0,'candidate heading'),sweep=transformedSweep(shared,scale,x,z,heading),pad=MALLARD_PILOT_LIMITS.obstacleMargin;
      const padded={min:sweep.min.map(v=>v-pad),max:sweep.max.map(v=>v+pad)};
      base.testedCandidates++;
      let reason='',obstacleId=null;
      if(padded.min[0]<-MALLARD_PILOT_LIMITS.glassX||padded.max[0]>MALLARD_PILOT_LIMITS.glassX||padded.min[2]<-MALLARD_PILOT_LIMITS.glassZ||padded.max[2]>MALLARD_PILOT_LIMITS.glassZ)reason='glass';
      else if(!lakeContainsRectangle(polygon,padded))reason='lake footprint';
      const bed=groundRange(surface,padded);
      const groundProbe=animalGroundAt(terrain,x,z);finite(groundProbe,'animalGroundAt probe');
      if(!reason&&sweep.min[1]-bed.max<MALLARD_PILOT_LIMITS.bedMargin)reason='insufficient continuous lake-bed clearance';
      if(!reason) {
        const hit=obstacles.find(o=>overlap(padded,o));
        if(hit){reason=hit.kind;obstacleId=hit.id;}
      }
      if(!reason&&probe) {
        const answer=probe(freeze({seed,x,z,heading,scale,sweptBounds:structuredClone(sweep)}));
        if(answer?.then||!answer||typeof answer.safe!=='boolean')fail('Invalid or asynchronous candidate probe result');
        if(!answer.safe)reason=typeof answer.reason==='string'&&answer.reason.trim()?answer.reason:'candidate probe rejected';
      }
      if(reason){base.rejections.push({candidate:base.testedCandidates-1,reason,obstacleId});continue;}
      const water=.18,rootY=water-.130*scale;
      return freeze({...base,safe:true,accepted:true,reason:'',omission:'',placement:{x,y:rootY,z,heading,pitch:0,roll:0,scale},support:{kind:'water',id:'lake-finite15a',position:[x,rootY,z],heading,waterlineMetres:.130},motion:{travelMetres:.204,travelScene:.204*scale,forward:'-Z',up:'+Y',finite:true,sweepIncludesTravel:true,initial:[x,z],end:[x-Math.sin(heading)*.204*scale,z-Math.cos(heading)*.204*scale]},water:{min:.169,max:.211,waveExcursion:.01015,rootMin:.169-.01015-.130*scale,rootMax:.211+.01015-.130*scale},sweptBoundsMetres:shared,reservedBounds:sweep,bed:{maximum:bed.max,minimumClearance:sweep.min[1]-bed.max,atAnchor:groundProbe,method:'exact animalGroundAt grid-cell vertex upper bound'},residentReservations:resident.obstacles,originalOmissions:resident.omitted});
    }
    return freeze({...base,reason:'No safe candidate in bounded water search',omission:'No safe candidate in bounded water search',originalOmissions:resident.omitted});
  } catch(error) {
    const reason=String(error?.message??error);
    return freeze({...base,reason,omission:reason});
  }
}
